import { ReactNode, useEffect } from "react";
import { matchPath, Navigate, useLocation } from "react-router-dom";
import { useSession } from "@clerk/clerk-react";

import { API_DOMAIN } from "@launchos/shared-utils/env";

import { returnToMyAccount } from "@/api/admin";
import { getBasicUserInfo, getSessionToken } from "@/api/auth";
import { checkLogin, endExpiredLogin, loginTimePassed } from "./loginCheck";
import { loginPathFor } from "./returnTo";

// LNK-5/6 (Oct 29): a saved login can be expired (logins last 24 hours from sign-in). Before
// this, an expired one opened the app and every page came up blank. Check it when the app
// opens, when the tab comes back, and every 5 minutes; an expired login logs out and the login
// screen returns here after sign-in.
const CHECK_EVERY_MS = 5 * 60 * 1000;

const askServer = async (token: string) => {
  const r = await fetch(`${API_DOMAIN}/sessions/${token}`, { headers: { Authorization: `Bearer ${token}` } });
  return { status: r.status, body: await r.json().catch(() => null) };
};

const useLoginWatch = (active: boolean) => {
  useEffect(() => {
    if (!active) return;
    const check = () =>
      checkLogin({ askServer }).then((v) => {
        if (v === "expired") endExpiredLogin({ returnToMyAccount });
      });
    check();
    const onShow = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onShow);
    const timer = setInterval(check, CHECK_EVERY_MS);
    return () => {
      document.removeEventListener("visibilitychange", onShow);
      clearInterval(timer);
    };
  }, [active]);
};

// LNK-1: the app's sign-in check. Signed in (regular login or Clerk) → the app. Signed out on a
// page that isn't public → the login screen, remembering the page so sign-in can return there.
// (Before this, a signed-out visit rendered nothing: a blank white page.) App pages are only
// registered for signed-in users with the right permission, so this can't sit on the pages
// themselves — it decides from the public list instead.
export const SignInGate = ({ publicPaths, children }: { publicPaths: string[]; children: ReactNode }) => {
  const { isLoaded, isSignedIn, session } = useSession();
  const { pathname, search, hash } = useLocation();

  const signedInWithOldLogin = Boolean(getSessionToken() && getBasicUserInfo() && !session?.user?.publicMetadata?.id);
  useLoginWatch(signedInWithOldLogin);
  if (signedInWithOldLogin && loginTimePassed()) return null; // expired: the watch is logging out
  if (signedInWithOldLogin || isSignedIn) return <>{children}</>;
  if (!isLoaded) return null; // Clerk is still checking — don't bounce someone who is signed in

  if (publicPaths.some((path) => matchPath(path, pathname))) return null;
  return <Navigate to={loginPathFor(pathname, search, hash)} replace />;
};
